import { useParams } from "@tanstack/react-router"
import { createColumnHelper, flexRender, Header, useTable } from "@tanstack/react-table"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { features, type Features } from "lib/table"
import { capitalize } from "lopermut"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { ConversationResponseResponseDataSchema, GetConversationsResponsesSortFields } from "schema"
import z from "zod"

interface CampaignResponsesTableProps {
  data: z.infer<typeof ConversationResponseResponseDataSchema>[]
  isPending: boolean
  search: { field?: string; order?: "asc" | "desc" }
  onSort: (field: (typeof GetConversationsResponsesSortFields)[number]) => void
}

const columnHelper = createColumnHelper<
  Features,
  z.infer<typeof ConversationResponseResponseDataSchema>
>()

export const CampaignResponsesTable = ({
  data,
  isPending,
  search,
  onSort,
}: CampaignResponsesTableProps) => {
  const { t } = useTranslation()
  const { id } = useParams({ strict: false }) as { id: string }

  const columns = useMemo(() => {
    return columnHelper.columns([
      columnHelper.accessor("goalName", {
        header: t("campaigns.responses.table.goal"),
        cell: (props) => <span>{props.row.original.goalDisplayName}</span>,
      }),
      columnHelper.accessor("customer.name", {
        header: t("campaigns.responses.table.customer"),
        cell: (props) => <span>{props.getValue() ?? "-"}</span>,
      }),
      columnHelper.accessor("response", {
        header: t("campaigns.responses.table.response"),
        cell: (props) => {
          const value = props.getValue()
          return <span>{value ? capitalize(value) : "-"}</span>
        },
      }),
      columnHelper.accessor("subject", {
        header: t("campaigns.responses.table.subject"),
        cell: (props) => {
          const value = props.getValue()
          return <span>{value ? capitalize(value) : "-"}</span>
        },
      }),
      columnHelper.accessor("createdAt", {
        header: t("campaigns.responses.table.createdAt"),
        cell: (props) => <DateDisplay date={props.getValue()} />,
        meta: {
          align: "right",
        },
      }),
    ])
  }, [t])

  const table = useTable({
    data: data ?? [],
    columns,
    features,
  })

  function buildSortable(
    header: Header<Features, z.infer<typeof ConversationResponseResponseDataSchema>, unknown>,
  ) {
    if (
      !GetConversationsResponsesSortFields.includes(
        header.id as (typeof GetConversationsResponsesSortFields)[number],
      )
    )
      return

    const isActive = search.field === header.id

    return {
      active: isActive,
      direction: isActive && search.order ? search.order : "desc",
      onClick: () => onSort(header.id as (typeof GetConversationsResponsesSortFields)[number]),
    }
  }

  function renderContent() {
    if (isPending) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (!data || data.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("campaigns.responses.table.empty")}
        />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow
        key={row.original.id}
        navigate={{
          to: "/campaigns/$id/activity/$customerId",
          params: { id, customerId: row.original.customerId },
          search: { section: "responses" },
        }}
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} {...cell.column.columnDef.meta} className="py-2">
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead
                key={header.id}
                {...header.column.columnDef.meta}
                sortable={buildSortable(header)}
              >
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>{renderContent()}</TableBody>
    </Table>
  )
}
